import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchAuthSession, getCurrentUser, signOut as amplifySignOut } from 'aws-amplify/auth';
import { deriveIsAdmin } from '../utils/rbac';

export const DEMO_USER_EMAIL = 'test@retainiq.demo';

/**
 * Core authentication state interface.
 * Contains the raw state values stored in React state.
 */
interface AuthState {
  user: any | null;
  token: string | null;
  isLoading: boolean;
  orgId: string | null;
  isDemoUser: boolean;
}

/**
 * Extended authentication context interface.
 * Includes derived properties and methods in addition to core state.
 */
interface AuthContextType extends AuthState {
  isAuthenticated: boolean;  // Derived: !!user
  isAdmin: boolean;          // Derived: deriveIsAdmin(token)
  signOut: () => Promise<void>;
  refreshToken: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isAuthenticated: false,
  isAdmin: false,
  isLoading: true,
  orgId: null,
  isDemoUser: false,
  signOut: async () => {},
  refreshToken: async () => {},
  checkAuth: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isLoading: true,
    orgId: null,
    isDemoUser: false,
  });

  const refreshToken = async () => {
    // Check if fetchAuthSession is available (might not be mocked in tests)
    if (typeof fetchAuthSession !== 'function') {
      throw new Error('fetchAuthSession is not available');
    }

    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      setAuthState(prev => ({ ...prev, token: idToken }));
      // Store token with timestamp for 8-hour timeout
      localStorage.setItem('auth_token', idToken);
      localStorage.setItem('auth_timestamp', Date.now().toString());
    } else {
      throw new Error('No token found in session');
    }
  };

  const checkAuth = async () => {
    try {
      // Check if token is expired (8 hours = 28800000 ms)
      const timestamp = localStorage.getItem('auth_timestamp');
      if (timestamp) {
        const elapsed = Date.now() - parseInt(timestamp);
        if (elapsed > 28800000) {
          // Token expired, sign out
          await signOut();
          setAuthState({ user: null, token: null, isLoading: false, orgId: null, isDemoUser: false });
          return;
        }
      }

      const currentUser = await getCurrentUser();

      // Try to refresh token and extract orgId from session claims
      let authToken = 'authenticated';
      let orgId: string | null = null;
      let isDemoUser = false;
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        if (idToken) {
          authToken = idToken.toString();
          localStorage.setItem('auth_token', authToken);
          localStorage.setItem('auth_timestamp', Date.now().toString());
          
          // Extract orgId and isDemoUser from JWT payload using manual parsing
          try {
            const payload = JSON.parse(atob(authToken.split('.')[1]));
            // Extract orgId from custom:orgId claim
            const rawOrgId = payload['custom:orgId'];
            orgId = typeof rawOrgId === 'string' && rawOrgId ? rawOrgId : null;
            // Extract isDemoUser from email claim
            const emailClaim = payload['email'];
            const loginId = currentUser?.signInDetails?.loginId;
            isDemoUser =
              loginId === DEMO_USER_EMAIL ||
              (typeof emailClaim === 'string' && emailClaim === DEMO_USER_EMAIL);
          } catch (parseError) {
            console.error('Failed to parse JWT payload:', parseError);
            // Continue with null orgId and false isDemoUser if parsing fails
          }
        }
      } catch (tokenError) {
        console.error('Token refresh failed, but user is authenticated:', tokenError);
        // Set a placeholder token if refresh fails but user exists
        // This handles test environments where fetchAuthSession might not be mocked
      }

      // CRITICAL FIX: Set user, token, and isLoading in a SINGLE state update
      // This ensures isAuthenticated is true when isLoading becomes false
      // Prevents the blank screen race condition by guaranteeing atomic state transition
      setAuthState({ user: currentUser, token: authToken, isLoading: false, orgId, isDemoUser });
    } catch (error: any) {
      // Silently handle UserPool misconfiguration (demo/dev environments)
      // and anonymous visitors — not signed in is an expected state, not an error
      if (
        error?.name === 'AuthUserPoolException' ||
        error?.name === 'UserUnAuthenticatedException' ||
        error?.message?.includes('UserPool') ||
        error?.message?.includes('not configured')
      ) {
        setAuthState({ user: null, token: null, isLoading: false, orgId: null, isDemoUser: false });
        return;
      }
      // Log genuinely unexpected auth errors only
      console.error('Auth check error:', error);
      setAuthState({ user: null, token: null, isLoading: false, orgId: null, isDemoUser: false });
    }
  };

  const signOut = async () => {
    try {
      await amplifySignOut();
      // Clear all auth state
      setAuthState({ user: null, token: null, isLoading: false, orgId: null, isDemoUser: false });
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_timestamp');
      // Clear any other cached data
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if sign out fails, clear local state
      setAuthState({ user: null, token: null, isLoading: false, orgId: null, isDemoUser: false });
      localStorage.clear();
      sessionStorage.clear();
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value = {
    user: authState.user,
    token: authState.token,
    // CRITICAL FIX: isAuthenticated should be true if user exists, regardless of token
    // This prevents blank screen when token refresh fails but user is authenticated
    isAuthenticated: !!authState.user,
    isAdmin: deriveIsAdmin(authState.token),
    isLoading: authState.isLoading,
    orgId: authState.orgId,
    isDemoUser: authState.isDemoUser,
    signOut,
    refreshToken,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
